import { ref, computed, useContext, useFetch, ssrRef } from "@nuxtjs/composition-api";
import {
  PROFESSION_PAGE_MODULES,
  DEFAULT_PROFESSION_PAGE_MODULES,
  getBaseSlug,
} from "~/config/professionPageModules";
import { SHARED_FRONT_PAGE_MODULE_SLUGS } from "~/config/sharedFrontPageModulesOnProfessionPage";
import {
  FRONT_PAGE_MODULES,
  getBaseSlug as getFrontPageBaseSlug,
} from "~/config/frontPageModules";

const isKnownSlug = (slug) =>
  slug in PROFESSION_PAGE_MODULES ||
  getBaseSlug(slug) in PROFESSION_PAGE_MODULES;

/**
 * Loads the active profession page module config for the given profession.
 *
 * Resolution (server-side, see backend spec):
 *   1. profession-specific override if active
 *   2. global default if active
 *   3. null → fall back to local DEFAULT_PROFESSION_PAGE_MODULES
 *
 * For modules whose slug is in SHARED_FRONT_PAGE_MODULE_SLUGS and whose
 * `content` is null, content is stitched in from the active front-page config.
 */
export default function useProfessionPageModules({ professionRef } = {}) {
  const { $query, $logger, $ab, $flags } = useContext();

  const modules = ssrRef([], "professionPageModules");
  const versionId = ssrRef(null, "professionPageVersionId");
  const isOverride = ssrRef(false, "professionPageIsOverride");
  const loading = ref(true);

  const sharedFrontPageContent = ssrRef(
    {},
    "professionPageSharedFrontPageContent"
  );

  const visibleModules = computed(() =>
    modules.value
      .filter((m) => m.isVisible && isKnownSlug(m.slug))
      .sort((a, b) => a.position - b.position)
      .map((m) => {
        const base = getBaseSlug(m.slug);
        const isSharedSlug =
          SHARED_FRONT_PAGE_MODULE_SLUGS.has(m.slug) ||
          SHARED_FRONT_PAGE_MODULE_SLUGS.has(base);
        if (isSharedSlug && (!m.content || Object.keys(m.content).length === 0)) {
          const inherited =
            sharedFrontPageContent.value[m.slug] ||
            sharedFrontPageContent.value[base];
          if (inherited) {
            return { ...m, content: inherited };
          }
        }
        return m;
      })
  );

  const getComponent = (slug) => {
    const loader =
      PROFESSION_PAGE_MODULES[slug] ||
      PROFESSION_PAGE_MODULES[getBaseSlug(slug)];
    if (!loader) {
      $logger?.warn(`Unknown profession page module slug: ${slug}`);
      return null;
    }
    return () => loader();
  };

  const loadFromApi = async () => {
    const profession = professionRef?.value;
    if (!profession) return false;

    let posthogExperimentId = null;
    let posthogVariant = null;

    if ($flags?.isFeatureEnabled("profession-page-ab-test")) {
      posthogExperimentId = "profession-page-module-order";
      if (
        $ab?.isVariant("profession-page-module-order", "test")
      ) {
        posthogVariant = "test";
      } else {
        posthogVariant = "control";
      }
    }

    const GET_CONFIG = await import(
      "~/components/_refactored/conductor/profession-page-modules/getActiveProfessionPageConfig.graphql"
    );
    const result = await $query(
      GET_CONFIG.default || GET_CONFIG,
      {
        professionId: profession.id || null,
        professionSeoName: profession.seoName || null,
        posthogExperimentId,
        posthogVariant,
      },
      {
        showErrorPage: false,
        showErrorToast: false,
      }
    );

    if (result?.activeProfessionPageConfig) {
      versionId.value = result.activeProfessionPageConfig.versionId;
      isOverride.value =
        !!result.activeProfessionPageConfig.isOverride;
      modules.value = result.activeProfessionPageConfig.modules.map((m) => ({
        ...m,
        versionId: result.activeProfessionPageConfig.versionId,
      }));
      return true;
    }
    return false;
  };

  const loadDefaults = () => {
    modules.value = DEFAULT_PROFESSION_PAGE_MODULES.map((m) => ({
      ...m,
      versionId: null,
    }));
  };

  const hasSharedSlugInUse = computed(() =>
    modules.value.some((m) => {
      const base = getBaseSlug(m.slug);
      return (
        SHARED_FRONT_PAGE_MODULE_SLUGS.has(m.slug) ||
        SHARED_FRONT_PAGE_MODULE_SLUGS.has(base)
      );
    })
  );

  const loadSharedFrontPageContent = async () => {
    if (!hasSharedSlugInUse.value) return;
    try {
      const GET_FRONT = await import(
        "~/components/_refactored/conductor/front-page-modules/getActiveFrontPageConfig.graphql"
      );
      const result = await $query(
        GET_FRONT.default || GET_FRONT,
        { posthogExperimentId: null, posthogVariant: null },
        { showErrorPage: false, showErrorToast: false }
      );
      const frontModules = result?.activeFrontPageConfig?.modules || [];
      const map = {};
      for (const fm of frontModules) {
        if (!fm.content) continue;
        map[fm.slug] = fm.content;
        const base = getFrontPageBaseSlug(fm.slug);
        if (base !== fm.slug && !map[base]) {
          map[base] = fm.content;
        }
      }
      sharedFrontPageContent.value = map;
    } catch (err) {
      $logger?.warn("Failed to load front page content for shared modules", err);
    }
  };

  // Validate that shared registry slugs actually exist in front-page registry
  const validateSharedRegistry = () => {
    for (const slug of SHARED_FRONT_PAGE_MODULE_SLUGS) {
      if (!FRONT_PAGE_MODULES[slug]) {
        $logger?.warn(
          `Shared front-page module slug "${slug}" is not registered in FRONT_PAGE_MODULES`
        );
      }
    }
  };

  useFetch(async () => {
    validateSharedRegistry();
    try {
      const loaded = await loadFromApi();
      if (!loaded) {
        loadDefaults();
      }
    } catch {
      loadDefaults();
    } finally {
      loading.value = false;
    }
    await loadSharedFrontPageContent();
  });

  return {
    modules,
    visibleModules,
    versionId,
    isOverride,
    loading,
    getComponent,
  };
}
